/**
 * Fantasy Console 0 - Map Editor Tab
 *
 * Full-featured map editor with tile placement, drawing tools,
 * and map manipulation capabilities.
 */

import { useState, useCallback, useEffect, useMemo } from 'react'
import { useCart } from '../../context/CartContext'
import { usePersistedTabState } from '../../context/EditorTabStateContext'
import {
  MAP_WIDTH,
  MAP_HEIGHT,
  SPRITE_SIZE,
  TOTAL_SPRITES,
  decodeBase64,
  createEmptySpriteSheet,
  createEmptyMap,
} from '@fc0/types'
import { useEditorHistory } from '../../hooks/useEditorHistory'
import { MapCanvas, Minimap, MapActions, MapTool } from '../MapEditor'
import { SpriteSheetPicker } from '../shared/SpriteSheetPicker'
import { ToolBar, type ToolDef } from '../shared/ToolBar'
import {
  PencilIcon,
  FillIcon,
  RectIcon,
  EraserIcon,
  PickerIcon,
} from '../shared/icons'
import styles from './MapEditorTab.module.css'
import editorStyles from '../../styles/editor.module.css'

const mapTools: ToolDef<MapTool>[] = [
  { id: 'pencil', label: 'Pencil', shortcut: 'P', icon: <PencilIcon /> },
  { id: 'fill', label: 'Fill', shortcut: 'F', icon: <FillIcon /> },
  { id: 'rect', label: 'Rectangle', shortcut: 'R', icon: <RectIcon /> },
  { id: 'eraser', label: 'Eraser', shortcut: 'E', icon: <EraserIcon /> },
  { id: 'picker', label: 'Tile Picker', shortcut: 'I', icon: <PickerIcon /> },
]

export function MapEditorTab() {
  const { cart, updateMap, cartLoadVersion } = useCart()

  // Editor state (persisted across tab switches)
  const [selectedTile, setSelectedTile] = usePersistedTabState(
    'map:selectedTile',
    0,
  )
  const [activeTool, setActiveTool] = usePersistedTabState<MapTool>(
    'map:activeTool',
    'pencil',
  )
  const [zoom, setZoom] = usePersistedTabState('map:zoom', 2)
  const [cameraX, setCameraX] = usePersistedTabState('map:cameraX', 0)
  const [cameraY, setCameraY] = usePersistedTabState('map:cameraY', 0)
  const [showGrid, setShowGrid] = usePersistedTabState('map:showGrid', true)

  // Non-persisted state
  const [cursorPos, setCursorPos] = useState<{ x: number; y: number } | null>(
    null,
  )

  // Convert sprites and map data to Uint8Array for components
  const spritesArray = useMemo(
    () =>
      cart.sprites ? decodeBase64(cart.sprites) : createEmptySpriteSheet(),
    [cart.sprites],
  )

  const mapData = useMemo(
    () => (cart.map ? decodeBase64(cart.map) : createEmptyMap()),
    [cart.map],
  )

  // Undo/redo history (64 entries)
  const {
    pushState: handleMapChange,
    undo,
    redo,
    resetHistory,
  } = useEditorHistory(updateMap)

  // Reset history when a new cart is loaded
  useEffect(() => {
    resetHistory(mapData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartLoadVersion, resetHistory])

  // Calculate viewport size in tiles
  const viewportWidth = useMemo(() => {
    const tileSize = SPRITE_SIZE * zoom
    return Math.ceil(768 / tileSize)
  }, [zoom])

  const viewportHeight = useMemo(() => {
    const tileSize = SPRITE_SIZE * zoom
    return Math.ceil(512 / tileSize)
  }, [zoom])

  // Handle tile picking from map
  const handleTilePick = useCallback((tileIndex: number) => {
    setSelectedTile(tileIndex)
    setActiveTool('pencil')
  }, [])

  // Handle camera change
  const handleCameraChange = useCallback((x: number, y: number) => {
    setCameraX(x)
    setCameraY(y)
  }, [])

  // Handle cursor position change
  const handleCursorChange = useCallback((x: number, y: number) => {
    setCursorPos({ x, y })
  }, [])

  // Handle minimap navigation
  const handleMinimapClick = useCallback((x: number, y: number) => {
    setCameraX(x)
    setCameraY(y)
  }, [])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if in input/textarea
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement ||
        (e.target instanceof HTMLElement && e.target.isContentEditable)
      ) {
        return
      }

      if (e.defaultPrevented) return

      const key = e.key.toLowerCase()

      // Undo/Redo shortcuts
      if (e.ctrlKey || e.metaKey) {
        if (key === 'z' && !e.shiftKey) {
          undo()
          e.preventDefault()
          return
        } else if (key === 'z' && e.shiftKey) {
          redo()
          e.preventDefault()
          return
        } else if (key === 'y') {
          redo()
          e.preventDefault()
          return
        }
      }

      // Tool shortcuts (no modifiers)
      if (!e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
        switch (key) {
          case 'p':
            setActiveTool('pencil')
            e.preventDefault()
            break
          case 'f':
            setActiveTool('fill')
            e.preventDefault()
            break
          case 'r':
            setActiveTool('rect')
            e.preventDefault()
            break
          case 'e':
            setActiveTool('eraser')
            e.preventDefault()
            break
          case 'i':
            setActiveTool('picker')
            e.preventDefault()
            break
          case '[':
            setSelectedTile((prev) => Math.max(0, prev - 1))
            e.preventDefault()
            break
          case ']':
            setSelectedTile((prev) => Math.min(TOTAL_SPRITES - 1, prev + 1))
            e.preventDefault()
            break
          case '=':
          case '+':
            setZoom((prev) => Math.min(4, prev + 1))
            e.preventDefault()
            break
          case '-':
            setZoom((prev) => Math.max(1, prev - 1))
            e.preventDefault()
            break
          case 'g':
            setShowGrid((prev) => !prev)
            e.preventDefault()
            break
        }

        // Arrow keys for camera panning
        const panSpeed = 4
        switch (e.key) {
          case 'ArrowLeft':
            setCameraX((prev) => Math.max(0, prev - panSpeed))
            e.preventDefault()
            break
          case 'ArrowRight':
            setCameraX((prev) =>
              Math.min(MAP_WIDTH - viewportWidth, prev + panSpeed),
            )
            e.preventDefault()
            break
          case 'ArrowUp':
            setCameraY((prev) => Math.max(0, prev - panSpeed))
            e.preventDefault()
            break
          case 'ArrowDown':
            setCameraY((prev) =>
              Math.min(MAP_HEIGHT - viewportHeight, prev + panSpeed),
            )
            e.preventDefault()
            break
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [zoom, undo, redo])

  return (
    <div className={editorStyles.editorTabContainer}>
      {/* Main Editor Area */}
      <div className={editorStyles.editorArea}>
        {/* Left: Map Canvas */}
        <div className={styles.mapCanvasPane}>
          <div className={styles.mapCanvasHeader}>
            <span className={editorStyles.panelLabel}>Map Editor</span>
            <span className={styles.info}>128x64 tiles</span>
          </div>
          <MapCanvas
            mapData={mapData}
            spriteData={spritesArray}
            selectedTile={selectedTile}
            activeTool={activeTool}
            zoom={zoom}
            cameraX={cameraX}
            cameraY={cameraY}
            showGrid={showGrid}
            onMapChange={handleMapChange}
            onCameraChange={handleCameraChange}
            onTilePick={handleTilePick}
            onCursorChange={handleCursorChange}
          />
          <div style={{ display: 'flex' }}>
            <MapActions
              mapData={mapData}
              spriteData={spritesArray}
              selectedTile={selectedTile}
              onMapChange={handleMapChange}
              zoom={zoom}
              onZoomChange={setZoom}
              showGrid={showGrid}
              onToggleGrid={() => setShowGrid((prev) => !prev)}
            />
          </div>
        </div>

        {/* Right: Tile Picker + Minimap */}
        <div className={styles.sidePanel}>
          <div className={styles.sidePanelContainer}>
            <SpriteSheetPicker
              sprites={spritesArray}
              selectedIndex={selectedTile}
              onSelect={setSelectedTile}
              scale={2}
              label='Tile Picker'
            />
            <ToolBar
              tools={mapTools}
              activeTool={activeTool}
              onSelectTool={setActiveTool}
              label=''
            />
          </div>
          <div className={styles.sidePanelContainer}>
            <Minimap
              mapData={mapData}
              spriteData={spritesArray}
              viewportX={cameraX}
              viewportY={cameraY}
              viewportWidth={viewportWidth}
              viewportHeight={viewportHeight}
              onViewportClick={handleMinimapClick}
            />
            <div className={styles.infoArea}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Camera</span>
                <span className={styles.infoValue}>
                  [{Math.floor(cameraX)}, {Math.floor(cameraY)}]
                </span>
              </div>
              {cursorPos && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Cursor</span>
                  <span className={styles.infoValue}>
                    [{cursorPos.x}, {cursorPos.y}]
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
